export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
}

export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  authorAvatar: string;
  date: string;
  readTime: string;
  imageSeq: string;
  imageQuery: string;
  tags: string[];
}

export const categories: Category[] = [
  {
    slug: 'knowledge',
    name: '人脉百科',
    description: '人脉管理的基础知识、方法论与最佳实践，帮助您建立系统的人脉管理认知。',
    icon: 'ri-book-open-line',
  },
  {
    slug: 'insights',
    name: '行业洞察',
    description: '职场关系趋势分析、行业人脉策略与前沿观点，让您始终站在行业前沿。',
    icon: 'ri-line-chart-line',
  },
  {
    slug: 'cases',
    name: '案例分享',
    description: '真实用户的人脉管理实践案例，从他人的经验中获取灵感与方法。',
    icon: 'ri-file-list-3-line',
  },
];

export const articles: Article[] = [
  {
    slug: 'how-to-build-professional-network',
    title: '职场新人如何从零开始构建高质量人脉网络',
    excerpt: '初入职场，人脉几乎为零？别担心，本文将从策略、方法和工具三个维度，帮你系统性地搭建自己的职场人脉体系。',
    content: `<h2>人脉不是认识多少人，而是多少人认可你</h2>
<p>很多职场新人有一个误区：认为人脉就是加微信、换名片。但真正有价值的人脉，是建立在相互认可和信任基础上的关系网络。在开始构建人脉之前，你需要先理解几个核心原则。</p>

<h2>第一步：明确定位，建立个人品牌</h2>
<p>在让别人认识你之前，先想清楚一个问题：你能为别人提供什么价值？这不是功利主义的算计，而是人脉关系的本质——价值互换。这里的"价值"包括专业能力、行业洞察、资源链接、甚至是一个有趣的聊天伙伴。</p>
<p>建议：</p>
<ul>
<li>在领英和脉脉上完善个人资料，展示专业能力</li>
<li>定期分享行业洞察和专业观点，建立个人影响力</li>
<li>参加行业活动时，准备好简洁有力的自我介绍</li>
</ul>

<h2>第二步：制定人脉拓展计划</h2>
<p>人脉拓展不能靠"碰运气"，需要有系统的方法和长期的坚持。建议制定每月的人脉拓展计划：</p>
<ul>
<li>每月至少参加 2 场线下行业活动</li>
<li>每周至少与 3 位新认识的人进行深入交流</li>
<li>在社交媒体上定期产出专业内容</li>
</ul>

<h2>第三步：用工具提升管理效率</h2>
<p>当人脉数量超过 100 人时，仅靠大脑记忆和微信备注已经不够用了。这时候需要借助专业的人脉管理工具，比如好联系，来自动整理联系人信息、记录互动历史、设置跟进提醒。</p>
<p>好工具能帮你做到：</p>
<ul>
<li>自动分类：将联系人按行业、重要性、关系类型自动归类</li>
<li>互动记录：记录每次联系的要点和后续行动</li>
<li>智能提醒：定时提醒你维护重要关系</li>
</ul>

<h2>第四步：维护关系比拓展更重要</h2>
<p>很多人热衷于认识新朋友，却忽视了维护已有关系。研究表明，维护一个老关系的 ROI 是拓展新关系的 3-5 倍。建议：</p>
<ul>
<li>重要联系人每季度至少互动一次</li>
<li>在对方的重要日子（生日、职业里程碑）送上祝福</li>
<li>分享对对方有价值的信息和资源</li>
</ul>

<h2>结语</h2>
<p>构建人脉网络是一个长期的过程，不要期待立竿见影的效果。从今天开始，用系统的方法和专业的工具，耐心经营你的每一段关系。记住——好联系，让每一次联系都更有价值。</p>`,
    category: 'knowledge',
    author: '李明远',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20author%20portrait%2C%20warm%20friendly%20expression%2C%20clean%20white%20background%2C%20editorial%20style%20headshot&width=80&height=80&seq=author-lmy&orientation=squarish',
    date: '2026-08-01',
    readTime: '8 分钟',
    imageSeq: 'article-network-01',
    imageQuery: 'Professional%20networking%20event%20scene%20with%20people%20exchanging%20business%20cards%20in%20modern%20conference%20room%2C%20warm%20lighting%2C%20professional%20atmosphere%2C%20clean%20minimalist%20composition',
    tags: ['职场新人', '人脉构建', '职业发展', '方法论'],
  },
  {
    slug: 'contact-management-tools-comparison',
    title: '2026年最佳人脉管理工具深度对比评测',
    excerpt: '市面上的联系人管理工具五花八门，如何选择最适合你的那一款？我们从功能、体验、价格等维度进行全面对比。',
    content: `<h2>为什么需要专业的人脉管理工具？</h2>
<p>在数字化时代，我们的社交关系分散在微信、钉钉、领英、邮箱等多个平台中。一个专业的人脉管理工具能够帮你把所有关系整合在一起，实现统一管理和智能维护。</p>

<h2>评测维度说明</h2>
<p>本次评测从以下五个维度进行对比：</p>
<ul>
<li><strong>联系人管理能力</strong>：导入、分类、标签、搜索的便捷性</li>
<li><strong>关系维护功能</strong>：互动记录、提醒系统、关系分析</li>
<li><strong>协作能力</strong>：团队共享、权限管理</li>
<li><strong>数据安全</strong>：加密级别、隐私保护</li>
<li><strong>性价比</strong>：功能与价格的匹配度</li>
</ul>

<h2>主流工具横向对比</h2>
<p>经过实际使用测试，好联系在综合体验上表现最佳，尤其是在智能分类和关系追踪方面有明显的优势。其 AI 去重准确率达到 99.5%，远超其他同类产品。</p>

<h2>选择建议</h2>
<p>对于个人用户，好联系免费版已经能满足基本需求。对于团队和企业用户，推荐使用专业版或企业版，解锁团队协作和高级分析功能。</p>`,
    category: 'knowledge',
    author: '陈晓芳',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20author%20portrait%2C%20warm%20smile%2C%20clean%20white%20background%2C%20editorial%20headshot&width=80&height=80&seq=author-cxf&orientation=squarish',
    date: '2026-07-28',
    readTime: '12 分钟',
    imageSeq: 'article-tools-02',
    imageQuery: 'Multiple%20contact%20management%20app%20interfaces%20displayed%20on%20screen%20side%20by%20side%20comparison%2C%20modern%20workspace%2C%20warm%20amber%20accent%2C%20professional%20product%20review%20setup',
    tags: ['工具评测', '人脉管理', '效率工具', '对比评测'],
  },
  {
    slug: 'network-is-asset',
    title: '为什么说人脉是职场人最重要的无形资产',
    excerpt: 'LinkedIn 创始人里德·霍夫曼曾说："你的人脉就是你最大的资产。"这句话在今天比任何时候都更真实。',
    content: `<h2>人脉的价值被严重低估</h2>
<p>在传统的职业发展观念中，我们往往过度强调硬技能的重要性，而忽视了人脉这个"软资产"的价值。但数据显示，85% 的职业机会通过人脉获得，65% 的商业合作源于原有关系的延伸。</p>

<h2>人脉的复利效应</h2>
<p>就像投资理财一样，人脉也有复利效应。你今天认识的一个普通朋友，可能在三年后成为你的重要合作伙伴。关键是——你需要有系统的方法来管理和维护这些关系。</p>

<h2>如何量化人脉价值</h2>
<p>好联系的人脉健康报告功能，能够帮你从连接广度、关系深度、互动频率等多个维度量化你的人脉资产价值，让"关系"这个模糊的概念变得可衡量、可优化。</p>`,
    category: 'insights',
    author: '王浩然',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20executive%20portrait%2C%20confident%20expression%2C%20clean%20white%20background%2C%20editorial%20headshot&width=80&height=80&seq=author-whr&orientation=squarish',
    date: '2026-07-25',
    readTime: '6 分钟',
    imageSeq: 'article-asset-03',
    imageQuery: 'Abstract%20visualization%20of%20professional%20network%20growing%20like%20a%20tree%20with%20golden%20connections%2C%20warm%20amber%20lighting%2C%20minimalist%20design%2C%20conceptual%20illustration%20style',
    tags: ['人脉价值', '职业发展', '思维认知', '行业洞察'],
  },
  {
    slug: 'sales-relationship-management',
    title: '销售总监实战分享：如何用人脉管理提升业绩40%',
    excerpt: '一位资深销售总监的真实经验：通过系统化的人脉管理，团队整体业绩提升了40%。他到底做了什么？',
    content: `<h2>从混乱到有序的转变</h2>
<p>作为管理 20 人销售团队的负责人，我最大的痛点是——客户信息分散在每个人的微信、Excel 和脑子里。当销售人员离职时，客户关系也跟着流失。直到我们引入了系统化的人脉管理方案。</p>

<h2>关键策略一：建立共享客户池</h2>
<p>使用好联系企业版建立共享通讯录后，所有客户信息统一管理。销售离职不再意味着客户流失，新人接手时可以查看完整的历史互动记录。</p>

<h2>关键策略二：设置智能跟进提醒</h2>
<p>根据不同客户类型设置差异化的跟进频率：A 类客户每周跟进、B 类客户每两周跟进、C 类客户每月跟进。系统自动推送提醒，大幅降低了遗忘率。</p>

<h2>成果：40% 的业绩提升</h2>
<p>实施 6 个月后，团队整体业绩提升了 40%，客户满意度也显著提高。这不仅仅是工具的作用，更是管理思维的升级。</p>`,
    category: 'cases',
    author: '王浩然',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20sales%20director%20portrait%2C%20confident%20expression%2C%20clean%20white%20background%2C%20editorial%20headshot&width=80&height=80&seq=author-whr2&orientation=squarish',
    date: '2026-07-20',
    readTime: '10 分钟',
    imageSeq: 'article-sales-04',
    imageQuery: 'Sales%20team%20meeting%20in%20modern%20office%20with%20dashboard%20showing%20performance%20metrics%20on%20large%20screen%2C%20professional%20atmosphere%2C%20warm%20lighting%2C%20clean%20minimalist%20composition',
    tags: ['销售管理', '实战案例', '业绩提升', '团队管理'],
  },
  {
    slug: 'freelancer-network-management',
    title: '自由职业者的人脉经营之道：从0到100个稳定客户',
    excerpt: '作为独立顾问，李婷玉分享了她如何通过系统性的人脉管理，从零开始建立起稳定的客户网络。',
    content: `<h2>自由职业者的困境</h2>
<p>自由职业者最大的挑战是什么？不是专业技能，而是稳定的客源。三年前我开始独立执业时，面临的就是这个问题——项目做完一个就没下一个，收入极不稳定。</p>

<h2>转折点：开始系统管理人脉</h2>
<p>我意识到，每一个合作过的客户、每一次活动认识的人，都是潜在的机会。但如果不做系统管理，这些关系就会随着时间淡忘。于是我开始使用好联系来管理我的所有联系人。</p>

<h2>具体做法</h2>
<ul>
<li>每次项目结束后，记录客户的反馈和偏好</li>
<li>按季度给老客户发送行业洞察邮件</li>
<li>定期查看关系图谱，发现潜在的转介绍机会</li>
<li>利用智能提醒，不错过任何重要客户的生日和纪念日</li>
</ul>

<h2>成果</h2>
<p>两年后，我积累了超过 100 个稳定合作客户，其中 70% 来自老客户转介绍。人脉管理不是成本，而是投资。</p>`,
    category: 'cases',
    author: '李婷玉',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20consultant%20portrait%2C%20warm%20smile%2C%20clean%20white%20background%2C%20editorial%20headshot&width=80&height=80&seq=author-lty&orientation=squarish',
    date: '2026-07-15',
    readTime: '7 分钟',
    imageSeq: 'article-freelancer-05',
    imageQuery: 'Freelancer%20working%20at%20cozy%20coffee%20shop%20with%20laptop%20showing%20contact%20management%20dashboard%2C%20warm%20atmosphere%2C%20natural%20light%2C%20clean%20minimalist%20composition',
    tags: ['自由职业', '客户管理', '实战经验', '个人品牌'],
  },
  {
    slug: 'ai-revolution-in-crm',
    title: 'AI时代的人脉管理：从被动记录到主动智能',
    excerpt: '人工智能正在重塑人脉管理的方式。本文将探讨 AI 如何让人脉管理从被动的信息记录，升级为主动的关系经营。',
    content: `<h2>传统 CRM 的局限</h2>
<p>传统的人脉管理工具本质上是一个"电子通讯录"——你输入什么，它就记录什么。但在 AI 时代，这远远不够。我们需要的不再是被动的信息仓库，而是主动的关系助手。</p>

<h2>AI 带来的三个变革</h2>
<h3>1. 智能洞察</h3>
<p>AI 能从你的互动数据中发现规律：谁是你最需要维护的关系？哪些联系人可能即将流失？AI 会主动告诉你答案，而不需要你手动分析。</p>
<h3>2. 智能推荐</h3>
<p>基于你的人脉网络，AI 可以推荐潜在的商业合作机会。"你认识 A，A 认识 B，而 B 恰好是你需要的合作伙伴"——这种"人脉六度空间"的推荐，AI 可以瞬间完成。</p>
<h3>3. 智能对话</h3>
<p>AI 助手可以根据历史互动记录，帮你起草个性化的问候信息，让每一次联系都恰到好处。</p>

<h2>好联系的 AI 实践</h2>
<p>好联系已经在产品中深度集成了 AI 能力，包括智能分类、商机推荐和关系健康评分。这让我们从"工具"进化为"顾问"。</p>`,
    category: 'insights',
    author: '李明远',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20tech%20founder%20portrait%2C%20warm%20friendly%20expression%2C%20clean%20white%20background%2C%20editorial%20headshot&width=80&height=80&seq=author-lmy2&orientation=squarish',
    date: '2026-07-10',
    readTime: '9 分钟',
    imageSeq: 'article-ai-06',
    imageQuery: 'Futuristic%20AI%20network%20visualization%20with%20data%20nodes%20and%20connections%20on%20dark%20screen%2C%20warm%20amber%20highlight%20accents%2C%20modern%20tech%20office%20environment%2C%20clean%20minimalist%20aesthetic',
    tags: ['AI', '技术趋势', '行业洞察', '产品创新'],
  },
  {
    slug: 'cross-industry-networking',
    title: '跨界人脉：如何在不同行业之间建立有价值的连接',
    excerpt: '最有价值的商业机会往往来自不同行业的交叉点。学习如何有策略地建立跨界人脉网络。',
    content: `<h2>跨界人脉的独特价值</h2>
<p>同一行业内的人脉往往拥有相似的资源和信息，而跨界人脉能带来全新的视角和机会。很多重大创新都发生在行业交叉的边界上。</p>

<h2>如何找到跨界连接点</h2>
<p>找到一个"连接主题"——可能是共同的兴趣爱好、相似的业务挑战，或是互补的资源需求。这个连接主题将是你跨界社交的"敲门砖"。</p>

<h2>维护跨界关系的技巧</h2>
<p>跨界关系因为日常交集少，更需要主动维护。使用好联系的智能提醒功能，定期与跨界联系人互动，分享对方可能感兴趣的行业动态。</p>`,
    category: 'insights',
    author: '陈晓芳',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20investor%20portrait%2C%20warm%20confident%20smile%2C%20clean%20white%20background%2C%20editorial%20headshot&width=80&height=80&seq=author-cxf2&orientation=squarish',
    date: '2026-07-05',
    readTime: '6 分钟',
    imageSeq: 'article-cross-07',
    imageQuery: 'Diverse%20professionals%20from%20different%20industries%20networking%20at%20modern%20event%20space%2C%20warm%20atmosphere%2C%20natural%20connections%2C%20clean%20minimalist%20composition',
    tags: ['跨界合作', '人脉拓展', '商业洞察', '社交策略'],
  },
  {
    slug: 'from-wechat-to-system',
    title: '从微信好友到系统人脉：数字化转型的第一步',
    excerpt: '你的微信有几千好友，但你能快速找到需要联系的人吗？本文教你如何将微信关系升级为系统化的人脉资产。',
    content: `<h2>微信好友 ≠ 有效人脉</h2>
<p>微信里有 5000 个好友，但当你真的需要找一个特定行业、特定背景的人时，往往需要在通讯录里翻半天。这就是"社交关系的数字化假象"——你以为你拥有这些关系，但实际上你无法有效利用它们。</p>

<h2>将关系"资产化"的三个步骤</h2>
<h3>第一步：导出和整理</h3>
<p>使用好联系一键导入微信联系人，自动识别和去重。</p>
<h3>第二步：标签和分类</h3>
<p>为每个联系人添加多维标签：行业、角色、认识场景、关系阶段等。</p>
<h3>第三步：建立互动系统</h3>
<p>设置定期跟进提醒，记录每次互动的要点，让关系持续"保鲜"。</p>

<h2>案例：从 3000 好友到精英人脉圈</h2>
<p>一位创业者通过以上方法，从 3000 个微信好友中筛选出 200 个核心联系人重点维护，一年内获得了 5 个重要合作机会和 2 笔投资意向。</p>`,
    category: 'knowledge',
    author: '赵敏仪',
    authorAvatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20manager%20portrait%2C%20bright%20warm%20smile%2C%20clean%20white%20background%2C%20editorial%20headshot&width=80&height=80&seq=author-zmy&orientation=squarish',
    date: '2026-06-28',
    readTime: '8 分钟',
    imageSeq: 'article-wechat-08',
    imageQuery: 'Smartphone%20displaying%20contact%20management%20app%20interface%20next%20to%20laptop%20showing%20organized%20contact%20database%2C%20warm%20lighting%2C%20clean%20workspace%2C%20minimalist%20composition',
    tags: ['微信', '数字化转型', '效率提升', '实用指南'],
  },
];